import {
  SKILL_FORMAT_LIMITS,
  type SkillFileInput,
  type SkillFileMediaType,
  SkillFileMediaTypeSchema,
} from '@parcel/types';
import { Button, Input } from '@parcel/ui';
import * as React from 'react';

/**
 * Raw-source editor for one skill bundle. Source stays in a labelled textarea;
 * this file never renders bundle content as markup and never parses markdown.
 */

export const RESERVED_SKILL_PATH = 'SKILL.md';

const MEDIA_TYPES = SkillFileMediaTypeSchema.options;

export type SkillPathCheck = { ok: true; path: string } | { ok: false; reason: string };

/** A relative, traversal-free path inside the bundle, or the reason it is not one. */
export function normalizeSkillPath(raw: string): SkillPathCheck {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { ok: false, reason: 'Enter a file path.' };
  if (trimmed.includes('\\')) {
    return { ok: false, reason: 'Use forward slashes in a file path.' };
  }
  if (trimmed.startsWith('/')) {
    return { ok: false, reason: 'Use a relative path, with no leading slash.' };
  }
  if (trimmed.length > SKILL_FORMAT_LIMITS.pathChars) {
    return { ok: false, reason: `A path is at most ${SKILL_FORMAT_LIMITS.pathChars} characters.` };
  }
  const segments = trimmed.split('/');
  if (segments.some((segment) => segment === '' || segment === '.' || segment === '..')) {
    return { ok: false, reason: 'A path cannot contain an empty, . or .. segment.' };
  }
  if (segments.some((segment) => segment.startsWith('.'))) {
    return { ok: false, reason: 'A path cannot contain a hidden segment that starts with a dot.' };
  }
  // The format counts folders, so the file name itself is one segment more.
  if (segments.length > SKILL_FORMAT_LIMITS.pathDepth + 1) {
    return {
      ok: false,
      reason: `A path is at most ${SKILL_FORMAT_LIMITS.pathDepth} folders deep.`,
    };
  }
  return { ok: true, path: trimmed };
}

/** A null byte or a replacement character means this was never UTF-8 text. */
export function hasBinaryContent(text: string): boolean {
  return text.includes('\u0000') || text.includes('\uFFFD');
}

export interface SkillBundleIssue {
  path: string | null;
  message: string;
}

function byteLength(text: string): number {
  return new TextEncoder().encode(text).length;
}

/** The bounds a browser can check without guessing at the server's parser. */
export function bundleIssues(files: readonly SkillFileInput[]): readonly SkillBundleIssue[] {
  const issues: SkillBundleIssue[] = [];
  const root = files.find((file) => file.path === RESERVED_SKILL_PATH);
  if (!root) issues.push({ path: null, message: `${RESERVED_SKILL_PATH} is required.` });
  else if (root.content.trim().length === 0) {
    issues.push({ path: RESERVED_SKILL_PATH, message: `${RESERVED_SKILL_PATH} needs content.` });
  }
  // The format caps supporting files; SKILL.md is not one of them.
  const supporting = files.filter((file) => file.path !== RESERVED_SKILL_PATH).length;
  if (supporting > SKILL_FORMAT_LIMITS.files) {
    issues.push({
      path: null,
      message: `A skill holds at most ${SKILL_FORMAT_LIMITS.files} files besides ${RESERVED_SKILL_PATH}.`,
    });
  }
  const seen = new Set<string>();
  let total = 0;
  for (const file of files) {
    const fold = file.path.toLowerCase();
    if (seen.has(fold)) {
      issues.push({ path: file.path, message: 'Two files share the same path.' });
    }
    seen.add(fold);
    const bytes = byteLength(file.content);
    total += bytes;
    if (bytes > SKILL_FORMAT_LIMITS.fileBytes) {
      issues.push({
        path: file.path,
        message: `${file.path} is larger than ${SKILL_FORMAT_LIMITS.fileBytes} bytes.`,
      });
    }
    if (hasBinaryContent(file.content)) {
      issues.push({ path: file.path, message: `${file.path} contains binary content.` });
    }
  }
  if (total > SKILL_FORMAT_LIMITS.bundleBytes) {
    issues.push({
      path: null,
      message: `This bundle is larger than ${SKILL_FORMAT_LIMITS.bundleBytes} bytes.`,
    });
  }
  return issues;
}

function caretPosition(value: string, index: number): { line: number; column: number } {
  const before = value.slice(0, index);
  const lines = before.split('\n');
  return { line: lines.length, column: (lines[lines.length - 1]?.length ?? 0) + 1 };
}

export interface SkillFilesEditorProps {
  files: readonly SkillFileInput[];
  onChange: (files: readonly SkillFileInput[]) => void;
  disabled?: boolean;
}

export function SkillFilesEditor({ files, onChange, disabled = false }: SkillFilesEditorProps) {
  const [addPath, setAddPath] = React.useState('');
  const [addMediaType, setAddMediaType] = React.useState<SkillFileMediaType>('text/markdown');
  const [error, setError] = React.useState<string | null>(null);
  const [renaming, setRenaming] = React.useState<string | null>(null);
  const addRef = React.useRef<HTMLInputElement>(null);

  const taken = (path: string, except?: string) =>
    files.some(
      (file) =>
        file.path.toLowerCase() === path.toLowerCase() &&
        file.path.toLowerCase() !== except?.toLowerCase(),
    );

  const replaceFile = (path: string, next: Partial<SkillFileInput>) => {
    onChange(files.map((file) => (file.path === path ? { ...file, ...next } : file)));
  };

  const handleAdd = () => {
    const checked = normalizeSkillPath(addPath);
    if (!checked.ok) {
      setError(checked.reason);
      return;
    }
    if (taken(checked.path)) {
      setError('Another file already uses that path.');
      return;
    }
    setError(null);
    setAddPath('');
    onChange([...files, { path: checked.path, mediaType: addMediaType, content: '' }]);
    addRef.current?.focus();
  };

  const handleRename = (from: string, to: string) => {
    const checked = normalizeSkillPath(to);
    if (!checked.ok) {
      setError(checked.reason);
      return;
    }
    if (taken(checked.path, from)) {
      setError('Another file already uses that path.');
      return;
    }
    setError(null);
    setRenaming(null);
    replaceFile(from, { path: checked.path });
  };

  return (
    <div className="space-y-4" data-testid="skill-files-editor">
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}

      {files.map((file) => (
        <SkillFileEditor
          key={file.path}
          file={file}
          disabled={disabled}
          renaming={renaming === file.path}
          onStartRename={() => setRenaming(file.path)}
          onCancelRename={() => setRenaming(null)}
          onRename={(next) => handleRename(file.path, next)}
          onRemove={() => onChange(files.filter((entry) => entry.path !== file.path))}
          onContent={(content) => replaceFile(file.path, { content })}
          onMediaType={(mediaType) => replaceFile(file.path, { mediaType })}
          onBinary={() => setError('That paste contains binary content, so it was not inserted.')}
        />
      ))}

      <div className="flex flex-wrap items-end gap-2 rounded-md border border-dashed p-3">
        <div className="space-y-1">
          <label className="text-xs font-medium" htmlFor="skill-new-file-path">
            New file path
          </label>
          <Input
            id="skill-new-file-path"
            value={addPath}
            disabled={disabled}
            onChange={(event) => setAddPath(event.target.value)}
            ref={addRef}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium" htmlFor="skill-new-file-media-type">
            New file media type
          </label>
          <select
            id="skill-new-file-media-type"
            className="h-9 rounded-md border px-2 text-sm"
            value={addMediaType}
            disabled={disabled}
            onChange={(event) => setAddMediaType(event.target.value as SkillFileMediaType)}
          >
            {MEDIA_TYPES.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>
        <Button type="button" size="sm" variant="outline" disabled={disabled} onClick={handleAdd}>
          Add file
        </Button>
      </div>
    </div>
  );
}

function SkillFileEditor({
  file,
  disabled,
  renaming,
  onStartRename,
  onCancelRename,
  onRename,
  onRemove,
  onContent,
  onMediaType,
  onBinary,
}: {
  file: SkillFileInput;
  disabled: boolean;
  renaming: boolean;
  onStartRename: () => void;
  onCancelRename: () => void;
  onRename: (next: string) => void;
  onRemove: () => void;
  onContent: (content: string) => void;
  onMediaType: (mediaType: SkillFileMediaType) => void;
  onBinary: () => void;
}) {
  const sourceId = React.useId();
  const mediaId = React.useId();
  const renameId = React.useId();
  const [caret, setCaret] = React.useState({ line: 1, column: 1 });
  const [renamePath, setRenamePath] = React.useState(file.path);
  const reserved = file.path === RESERVED_SKILL_PATH;

  return (
    <section className="space-y-2 rounded-md border p-3" data-testid="skill-file-editor">
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-xs font-medium" htmlFor={sourceId}>
          {`${file.path} source`}
        </label>
        <label className="sr-only" htmlFor={mediaId}>
          {`${file.path} media type`}
        </label>
        <select
          id={mediaId}
          className="h-8 rounded-md border px-2 text-xs"
          value={file.mediaType}
          disabled={disabled}
          onChange={(event) => onMediaType(event.target.value as SkillFileMediaType)}
        >
          {MEDIA_TYPES.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
        {reserved ? null : (
          <>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={disabled}
              onClick={() => {
                setRenamePath(file.path);
                onStartRename();
              }}
            >
              {`Rename ${file.path}`}
            </Button>
            <Button type="button" size="sm" variant="ghost" disabled={disabled} onClick={onRemove}>
              {`Remove ${file.path}`}
            </Button>
          </>
        )}
      </div>

      {renaming ? (
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <label className="text-xs font-medium" htmlFor={renameId}>
              {`New path for ${file.path}`}
            </label>
            <Input
              id={renameId}
              value={renamePath}
              onChange={(event) => setRenamePath(event.target.value)}
            />
          </div>
          <Button type="button" size="sm" variant="outline" onClick={() => onRename(renamePath)}>
            Save new path
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={onCancelRename}>
            Cancel rename
          </Button>
        </div>
      ) : null}

      <textarea
        id={sourceId}
        rows={12}
        spellCheck={false}
        disabled={disabled}
        className="w-full rounded-md border p-2 font-mono text-xs"
        value={file.content}
        onPaste={(event) => {
          const pasted = event.clipboardData.getData('text');
          if (!hasBinaryContent(pasted)) return;
          event.preventDefault();
          onBinary();
        }}
        onChange={(event) => {
          if (hasBinaryContent(event.target.value)) {
            onBinary();
            return;
          }
          setCaret(caretPosition(event.target.value, event.target.selectionStart ?? 0));
          onContent(event.target.value);
        }}
        onKeyUp={(event) =>
          setCaret(
            caretPosition(event.currentTarget.value, event.currentTarget.selectionStart ?? 0),
          )
        }
        onClick={(event) =>
          setCaret(
            caretPosition(event.currentTarget.value, event.currentTarget.selectionStart ?? 0),
          )
        }
      />
      <p className="text-xs text-muted-foreground" data-testid={`skill-file-caret-${file.path}`}>
        {`Line ${caret.line}, Column ${caret.column}`}
      </p>
    </section>
  );
}
